import type {TaillightsBlockConfig, TaillightsBlockPaint, TaillightsStylingDirective} from './taillights-worker'
import type {TaillightsStyleRun} from './taillights-styles'

const MAX_GEOMETRY_LINE_LENGTH = 2000
// JS has no Bidi_Class escape, so conservatively cover Unicode 17 RTL blocks, including reserved code points.
// https://www.unicode.org/Public/17.0.0/ucd/extracted/DerivedBidiClass.txt
const UNSUPPORTED_BIDI_PATTERN =
  /[\p{Bidi_Control}\u0590-\u08ff\ufb1d-\ufdff\ufe70-\ufeff\u{10800}-\u{10fff}\u{1e800}-\u{1efff}]/u

/**
 * Whether a line's columns map to x positions by measuring its prefix: false for very long lines, which would cost too
 * much to measure, and for right-to-left or bidi-control text, whose visual order differs from its logical order.
 */
export function hasLineGeometry(line: string) {
  return line.length <= MAX_GEOMETRY_LINE_LENGTH && !UNSUPPORTED_BIDI_PATTERN.test(line)
}

/** Original zero-based source line and UTF-16 column, not a visual or pixel column. */
export type SourcePoint = {line: number; column: number}
/** A source range with an exclusive end position. */
export type SourceRange = {start: SourcePoint; end: SourcePoint}
export type SourceSelection = SourceRange & {direction: 'forward' | 'backward' | 'none'}
export type FoldSection = {startLine: number; endLine: number; index: number; level: number}

export type SourceDocument = {
  id: string
  lines: readonly string[]
  directives?: TaillightsStylingDirective[][]
  sections?: readonly FoldSection[]
}

export type RenderingProfile = Pick<
  TaillightsBlockConfig,
  'font' | 'fonts' | 'lineHeight' | 'tabSize' | 'defaultColor' | 'colors'
> & {paddingInline: number}

export type Palette = Pick<RenderingProfile, 'defaultColor' | 'colors'>
export type GlyphRun = TaillightsStyleRun & {nativeText?: boolean}

export type PreparedText = {
  text: string
  lines: readonly string[]
  lineNumbers: readonly number[]
  lineStarts: readonly number[]
  sourceToDisplay: ReadonlyMap<number, number>
  styleRuns: readonly TaillightsStyleRun[]
  glyphRuns: readonly GlyphRun[]
}

type PreparedBlockContent = PreparedText & {
  id: string
  anchor: number
  imageKey: string
  readonly palette: Palette
  displayStart: number
  width: number
  height: number
}

export type PreparedPaintBlock = PreparedBlockContent & {paint: 'none' | 'bitmap' | 'spans'}

export type PreparedBlock =
  | PreparedPaintBlock
  | (PreparedBlockContent & {
      paint: 'mixed'
      /** Contiguous paint groups within the logical chunk; native selection still uses the complete block. */
      parts: readonly PreparedPaintBlock[]
    })

export type PreparedView = {
  id: string
  text: string
  lines: readonly string[]
  lineNumbers: readonly number[]
  lineStarts: readonly number[]
  sourceToDisplay: ReadonlyMap<number, number>
  sourceLineStarts: readonly number[]
  sourceLineLengths: readonly number[]
  blocks: readonly PreparedBlock[]
  collapsed: readonly number[]
  width: number
  height: number
  selection: SourceSelection | null
  nativeSelection: {start: number; end: number; direction: SourceSelection['direction']} | null
}

export type SearchResult = {
  query: string
  /** Number of retained matches, capped at 200. */
  count: number
  index: number
  current: SourceRange | null
  highlights: readonly SourceRange[]
}

export type SourceSymbol = {
  text: string
  line: number
  startColumn: number
  endColumn: number
}

export type SourceRect = {
  line: number
  startColumn: number
  endColumn: number
  x: number
  y: number
  width: number
  height: number
}

export type ProjectRequest = {
  collapsed: readonly number[]
  linesPerChunk?: number
  palette?: Palette
  selection?: SourceSelection | null
  expandSelection?: boolean
  /** The range that `expandSelection` unfolds instead of `selection`, such as a linked range whose caret waits at its start. */
  reveal?: SourceRange | null
}

export type DocumentOperations = {
  initialize: {
    input: {source: SourceDocument; profile: RenderingProfile}
    output: {sourceLineCount: number; sourceLineStarts: readonly number[]; sourceLineLengths: readonly number[]}
  }
  project: {input: ProjectRequest; output: PreparedView}
  block: {input: {lineNumbers: readonly number[]; palette?: Palette}; output: PreparedBlock}
  render: {input: {blockId: string}; output: TaillightsBlockPaint}
  search: {input: {query: string; kind: 'text' | 'symbol'; index: number}; output: SearchResult}
  inspect: {input: {selection: SourceSelection}; output: SourceSymbol | null}
  rects: {
    input: {ranges: readonly SourceRange[]; collapsed: readonly number[]; blockId?: string}
    output: readonly SourceRect[]
  }
}

export type DocumentOperation = keyof DocumentOperations
export type DocumentRequest = {
  [K in DocumentOperation]: {type: K; requestId: number; input: DocumentOperations[K]['input']}
}[DocumentOperation]

export type DocumentResponse =
  | {
      [K in DocumentOperation]: {type: K; requestId: number; ok: true; value: DocumentOperations[K]['output']}
    }[DocumentOperation]
  | {type: DocumentOperation; requestId: number; ok: false; error: string; cancelled?: boolean}

export type WorkerDocumentMessage = DocumentRequest | {type: 'cancel'; requestIds: readonly number[]}
